import { toast } from 'react-toastify';
import CopyToClipboardButton from '../components/copy-button';
import RandomImage from '../components/random-image';
import { v4 as uuid } from 'uuid';
import { IoClose } from 'react-icons/io5';

//styles
const randomImageStyle = {
  position: 'absolute',
  height: 70,
  right: 0,
  top: 0,
  opacity: 0.3,
  zIndex: -1,
};

// Default settings for toast notifications
const defaultSettings = {
  position: 'bottom-left',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: 'colored',
  update: true,
  icon: false,
};

// Custom close button component for toasts
const CloseButton = ({ closeToast }) => (
  <i
    data-testid='close-button'
    className='Toastify__close-button'
    style={{ height: 15, width: 15 }}
    onClick={closeToast}>
    <IoClose />
  </i>
);

// Default settings for error toasts
const defaultErrorOptions = {
  allowCopy: true,
  closeButton: CloseButton,
  closeOnClick: false,
  autoClose: true,
  ...defaultSettings,
};

// Types of toasts
const toastTypes = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  MESSAGE: 'message',
};

// Function to calculate the width of a text string
function getTextWidth(text, font) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = font || getComputedStyle(document.body).font;
    return context.measureText(text).width;
  }
  return 0;
}

// Function to calculate the width of an error message
function getErrorWidth(text, title = null, copyToClipboard) {
  const isArray = Array.isArray(text);
  if (!isArray) {
    return text;
  }
  const maxWidth = 800;
  const width = text.reduce(function (width, errorMessage) {
    const nextWidth = getTextWidth(errorMessage, '20px Roboto sans-serif');
    if (width < nextWidth) {
      return Math.min(nextWidth, maxWidth);
    }
    return width;
  }, 320);
  const titleWidth = title ? getTextWidth(title, '24px sans-serif') : 0;
  return {
    width: `${
      titleWidth > width ? titleWidth : width + (copyToClipboard ? 50 : 0)
    }px`,
  };
}

// Function to determine the display color based on message types
function getDisplayColor(messages) {
  const hasError = messages.some((m) => m.type === toastTypes.ERROR);
  const hasWarning = messages.some((m) => m.type === toastTypes.WARNING);
  const hasMessage = messages.some((m) => m.type === toastTypes.MESSAGE);
  if (hasError) {
    return 'error';
  }
  if (hasWarning) {
    return 'warning';
  }
  if (hasMessage) {
    return 'info';
  }
  return 'white';
}

// Function to render the messages in the toast
function renderMessages(messages, title, copyToClipboard, color) {
  const render = (
    <div className='toastContainer' style={{ overflow: 'hidden !important' }}>
      <div>
        <div>
          {title ? (
            <span>
              <h5
                style={{
                  marginBottom: messages.length === 0 ? '0' : '8px',
                  color: `var(--${color})`,
                }}>
                {title}
              </h5>
            </span>
          ) : null}
          {messages.map((e, index) => {
            if (!e.text) {
              /* istanbul ignore next */
              return null;
            }
            if (index < 5) {
              return (
                <span
                  key={index}
                  style={{ display: 'flex', alignItems: 'center' }}>
                  <RandomImage style={randomImageStyle} />
                  {e.text} <br />
                </span>
              );
            } else if (index === messages.length - 1) {
              return `... and ${messages.length - 5} more`;
            } else {
              /* istanbul ignore next */
              return null;
            }
          })}
        </div>
      </div>
      {copyToClipboard && (
        <div className="copy-toast-button">
          <CopyToClipboardButton
            text={messages.map((m) => `[${m.type}]: ${m.text}`).join('\n')}
          />
        </div>
      )}
    </div>
  );
  return render;
}

const ToastService = (function () {
  // Function to display a success toast
  function success(text, toastId = uuid(), options = defaultSettings) {
    return displayToast({
      toastId,
      data: { message: text },
      type: toastTypes.SUCCESS,
      options,
    });
  }

  // Function to display an error toast
  function error(text, toastId = uuid(), options = defaultErrorOptions) {
    return displayToast({
      toastId,
      data: { message: text },
      type: toastTypes.ERROR,
      options,
    });
  }

  // Function to display a warning toast
  function warn(
    text,
    toastId = uuid(),
    options = defaultErrorOptions,
    title = null
  ) {
    return displayToast({
      toastId,
      title,
      data: { message: text },
      type: toastTypes.WARNING,
      options,
    });
  }

  // Function to display an info toast
  function info(
    text,
    toastId = uuid(),
    options = defaultSettings,
    title = null
  ) {
    return displayToast({
      toastId,
      data: { message: text },
      title,
      type: toastTypes.INFO,
      options,
    });
  }

  // Function to display a toast with custom options
  function displayToast({
    toastId,
    data,
    title,
    type,
    options = {},
    copyToClipboard,
  }) {
    let messages = [];
    let errors = false;
    let header = title || options.title;
    if (typeof data === 'object') {
      messages = Array.from(
        new Set(
          [
            ...[{ type: toastTypes.ERROR, text: data.error }],
            ...[].concat(data.errors).map((m) => {
              return { type: toastTypes.ERROR, text: m };
            }),
            ...[].concat(data.warnings).map((m) => {
              return { type: toastTypes.WARNING, text: m };
            }),
            ...[].concat(data.messages).map((m) => {
              return { type: toastTypes.MESSAGE, text: m };
            }),
          ].filter((m) => m.text)
        )
      );

      if (messages.length === 0) {
        messages = [].concat(data.message).map((m) => {
          return { type, text: m };
        });
      } else {
        /* istanbul ignore next */
        header = data.message || header;
      }
      errors = data.success;
    } else {
      /* istanbul ignore next */
      messages = [{ type, text: data }];
    }

    const width = getErrorWidth(
      messages.slice(0, 5).map((m) => m.text),
      header,
      copyToClipboard
    );
    const autoClose = errors && messages.length > 1 ? false : options.autoClose;
    const fullOptions = {
      ...defaultErrorOptions,
      ...options,
      copyToClipboard,
      style: width,
      autoClose: !autoClose
        ? autoClose
        : autoClose || defaultErrorOptions.autoClose,
      isLoading: false,
    };

    if (type) {
      fullOptions.type = type;
    }
    const isActive = toast.isActive ? toast.isActive(toastId) : false;
    if (isActive) {
      return toast.update(toastId, {
        ...fullOptions,
        render: renderMessages(
          messages,
          header,
          copyToClipboard,
          getDisplayColor(messages)
        ),
      });
    }
    const toaster = renderMessages(
      messages,
      header,
      copyToClipboard,
      getDisplayColor(messages)
    );
    return toast(toaster, {
      ...defaultSettings,
      toastId,
      ...fullOptions,
      type,
    });
  }

  return {
    success,
    warn,
    error,
    info,
    displayToast,
    toastTypes,
  };
})();
export default ToastService;
